import { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useHistory } from "react-router-dom";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Flex,
  Span,
  Table,
  Button,
  TableRow,
  useModal,
  NoResults,
  TableBody,
  TableCell,
  TableHead,
  PageWrapper,
  StretchPanel,
  TableHeadCell,
} from "@ubisend/pulse-components";

import { EditLicense, CreateLicense, LicenseClientCard } from "../Components";
import { deleteLicense as deleteLicenseApi } from "../api";

dayjs.extend(relativeTime);

const Licenses = ({ match: { params }, location: { state: name } }) => {
  const history = useHistory();

  const [createLicense, setCreateLicense] = useState(false);

  const [editLicense, setEditLicense] = useState(false);

  const { showModal, hideModal } = useModal();

  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: [`licenses/${params.projectId}`] });

  const handleExpiry = ({ status, period }) => {
    if (status && !period?.end) {
      return <Span>Never expires</Span>;
    }

    if (period?.end) {
      return (
        <Flex col>
          <Span>{dayjs(period.end).format("DD/MM/YYYY")}</Span>
          <Span light>{dayjs(period.end).from(dayjs())}</Span>
        </Flex>
      );
    }
  };

  const { mutate: deleteLicense } = useMutation({
    mutationFn: deleteLicenseApi,
  });

  const handleDestroy = (license) => {
    showModal({
      header: "Delete a license",
      body: `Are you sure you want to delete this license? If it's linked to a client, their bots will immediately stop working. Make sure you know what you're doing.`,
      handleConfirm: async () => {
        try {
          deleteLicense({
            project_id: params.projectId,
            license_id: license.license_id,
          });

          queryClient.invalidateQueries({
            queryKey: [`licenses/${params.projectId}`],
          });
        } finally {
          hideModal();
        }
      },
    });
  };

  return (
    <>
      <PageWrapper
        header="Licenses"
        subheader={`Manage licenses for ${name?.name ?? "your install"}`}
        actions={
          <Button
            icon="arrowNarrowLeft"
            variant="primary"
            onClick={() => history.goBack()}
          >
            Back
          </Button>
        }
      >
        {createLicense && (
          <CreateLicense
            account_id={createLicense}
            project_id={params.projectId}
            handleCancel={() => setCreateLicense(false)}
          />
        )}

        {editLicense && (
          <EditLicense
            project_id={params.projectId}
            license={editLicense}
            handleCancel={() => setEditLicense(false)}
          />
        )}

        {query.isSuccess && query.data.data.length === 0 && (
          <NoResults
            text="No projects found"
            subtitle="Why not create a shiny new one"
          />
        )}

        {query.isError && (
          <NoResults
            text="There was a problem connecting to the project"
            subtitle="Double check the projects credentials or beg a developer for help"
          />
        )}

        {query.isSuccess && query.data.data.length > 0 && (
          <StretchPanel>
            <Table loading={query.isLoading} loadingColumns={4}>
              <TableHead>
                <TableRow>
                  <TableHeadCell>Account information</TableHeadCell>
                  <TableHeadCell>Exact expiry</TableHeadCell>
                </TableRow>
              </TableHead>
              {!query.isLoading && query.data?.data.length > 0 && (
                <TableBody>
                  {query.data.data.map((license, key) => (
                    <TableRow key={key}>
                      <TableCell>
                        <LicenseClientCard
                          client={{
                            logo: null,
                            name: license.account_name,
                            license,
                          }}
                        />
                      </TableCell>
                      <TableCell>{handleExpiry(license)}</TableCell>
                      <TableCell>
                        {!license.status && (
                          <Flex xSpace right>
                            <Button
                              icon="plus"
                              onClick={() =>
                                setCreateLicense(license.account_id)
                              }
                            >
                              Add license
                            </Button>
                          </Flex>
                        )}
                        {license.status && (
                          <Flex xSpace right>
                            <Button
                              icon="cog"
                              onClick={() => setEditLicense(license)}
                            >
                              Manage
                            </Button>
                            <Button
                              colour="danger"
                              icon="trash"
                              onClick={() => handleDestroy(license)}
                              variant="secondary"
                            >
                              Remove
                            </Button>
                          </Flex>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </StretchPanel>
        )}
      </PageWrapper>
    </>
  );
};

export default Licenses;
